<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <div class="small text-muted"><router-link to="/authority-request">{{ $t('arTitle') }}</router-link> / {{ isNew ? $t('arNew') : (doc && doc.code) }}</div>
        <h1 v-if="isNew"><i class="fa fa-balance-scale"></i> {{ $t('arNew') }}</h1>
        <h1 v-else-if="doc"><i class="fa fa-balance-scale"></i> {{ doc.code }} · {{ sourceLabel(doc.source) }}<span v-if="doc.sourceName"> · {{ doc.sourceName }}</span>
          <span v-if="doc.reference" class="small text-muted"> · {{ doc.reference }}</span>
        </h1>
      </div>
      <div v-if="doc">
        <span v-if="doc.status === 'answered'" class="badge badge-success p-2">{{ $t('arAnswered') }} {{ doc.answeredAt | moment('DD/MM HH:mm') }}</span>
        <span v-else-if="lookupProgress" class="badge badge-info p-2">{{ $t('arLookingUp', [lookupProgress.done, lookupProgress.total]) }}</span>
      </div>
    </div>

    <!-- các bước -->
    <div class="d-flex mb-3 ar-steps">
      <div :class="['ar-step', step === 1 ? 'on' : (step > 1 ? 'done' : '')]" @click="!isNew && (step = 1)"><span class="n">1</span>{{ $t('arStep1') }}</div>
      <div :class="['ar-step', step === 2 ? 'on' : (step > 2 ? 'done' : '')]" @click="!isNew && (step = 2)"><span class="n">2</span>{{ $t('arStep2') }}</div>
      <div :class="['ar-step', step === 3 ? 'on' : '']" @click="!isNew && (step = 3)"><span class="n">3</span>{{ $t('arStep3') }}</div>
    </div>
    <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

    <!-- ================= BƯỚC 1: TIẾP NHẬN ================= -->
    <div v-if="step === 1">
      <div class="tile">
        <div class="row">
          <div class="col-md-2"><b-form-group :label="$t('arSource')"><b-form-select v-model="form.source" :options="sourceOptions" :disabled="!isNew"></b-form-select></b-form-group></div>
          <div class="col-md-2"><b-form-group :label="$t('arSourceName')"><b-form-input v-model="form.sourceName" :disabled="!isNew" placeholder="Leeon, Minh Phúc, PA05…"></b-form-input></b-form-group></div>
          <div class="col-md-3"><b-form-group :label="$t('arType')"><b-form-select v-model="form.requestType" :options="typeOptions" :disabled="!isNew"></b-form-select></b-form-group></div>
          <div class="col-md-3"><b-form-group :label="$t('arReference')"><b-form-input v-model="form.reference" :disabled="!isNew"></b-form-input></b-form-group></div>
          <div class="col-md-2"><b-form-group :label="$t('arDueTime')"><b-form-input type="datetime-local" v-model="form.dueLocal" :disabled="!isNew"></b-form-input></b-form-group></div>
        </div>
        <div class="mb-2 small" v-if="isNew">
          <span class="text-muted mr-1">{{ $t('arQuickType') }}:</span>
          <b-button v-for="t in ['recordingsByCdr', 'recordingsByNumber', 'bulkIdentify', 'identify']" :key="t" size="sm" :variant="form.requestType === t ? 'primary' : 'outline-secondary'" class="mr-1 mb-1" @click="form.requestType = t">{{ $t('arType_' + t) }}</b-button>
        </div>
        <div class="row" v-if="form.requestType !== 'recordingsByNumber'">
          <div class="col-md-3">
            <b-form-group :label="$t('arSourceNumber')">
              <b-input-group>
                <b-form-input v-model="form.sourceNumber" :disabled="!isNew" placeholder="510117"></b-form-input>
                <b-input-group-append><b-form-select v-model="poolPick" :options="poolOptions" :disabled="!isNew" @change="form.sourceNumber = poolPick || form.sourceNumber"></b-form-select></b-input-group-append>
              </b-input-group>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group :label="$t('arTicket')">
              <b-input-group>
                <b-form-input v-model="form.ticketCode" :disabled="!isNew" placeholder="T-13641" @keydown.enter="loadTicket"></b-form-input>
                <b-input-group-append><b-button variant="outline-secondary" :disabled="!isNew" @click="loadTicket">{{ $t('arTicketLoad') }}</b-button></b-input-group-append>
              </b-input-group>
              <div v-if="ticketExisting.length" class="small text-warning mt-1">{{ $t('arTicketExisting', [ticketExisting.map(e => e.code).join(', ')]) }}</div>
            </b-form-group>
          </div>
        </div>
        <div class="row" v-else>
          <div class="col-md-4"><client-select :model="form.scope" attribute="client" :label="$t('arScopeClient')" :disabled="!isNew"></client-select></div>
          <div class="col-md-3"><b-form-group :label="$t('arScopeNumber')"><b-form-input v-model="form.scope.number" :disabled="!isNew" placeholder="0996617409"></b-form-input></b-form-group></div>
          <div class="col-md-2"><b-form-group :label="$t('arScopeFrom')"><b-form-input type="date" v-model="form.scope.fromLocal" :disabled="!isNew"></b-form-input></b-form-group></div>
          <div class="col-md-2"><b-form-group :label="$t('arScopeTo')"><b-form-input type="date" v-model="form.scope.toLocal" :disabled="!isNew"></b-form-input></b-form-group></div>
          <div class="col-12 small text-muted">{{ $t('arScopeHint') }}</div>
        </div>
      </div>

      <div class="row" v-if="form.requestType !== 'recordingsByNumber'">
        <div class="col-lg-5">
          <div class="tile">
            <h5 class="small text-uppercase text-muted">{{ $t('arPaste') }}</h5>
            <b-form-textarea v-model="form.rawText" rows="12" :disabled="!isNew" style="font-family: monospace; font-size: 12.5px"></b-form-textarea>
            <div class="mt-2 d-flex align-items-center flex-wrap">
              <b-button variant="primary" class="mr-2 mb-1" :disabled="!isNew || !form.rawText" @click="parseText">{{ $t('arSplit') }}</b-button>
              <span class="text-muted mr-2 mb-1">{{ $t('arOr') }}</span>
              <label class="btn btn-secondary mb-1" :class="{disabled: !isNew}">
                <i class="fa fa-upload"></i> {{ $t('arUpload') }}
                <input type="file" accept=".xlsx,.xls" hidden :disabled="!isNew" @change="parseFile">
              </label>
              <span v-if="form.inputFileName" class="small text-muted ml-2 mb-1">{{ form.inputFileName }}</span>
            </div>
          </div>
        </div>
        <div class="col-lg-7">
          <div class="tile">
            <h5 class="small text-uppercase text-muted">{{ $t('arParsed') }} ({{ form.items.length }})</h5>
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <thead><tr><th>#</th><th v-if="hasCaller">{{ $t('arColCaller') }}</th><th>{{ $t('arColNumber') }}</th><th>{{ $t('arColTime') }}</th><th>{{ $t('arColType') }}</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="(it, i) in form.items" :key="i" :class="{'table-warning': it.warnings && it.warnings.length}">
                    <td>{{ i + 1 }}</td>
                    <td v-if="hasCaller"><b-form-input v-model="it.caller" size="sm" :disabled="!isNew"></b-form-input></td>
                    <td>
                      <b-form-input v-model="it.reportedNumber" size="sm" :disabled="!isNew" style="min-width: 130px"></b-form-input>
                      <span v-if="hasWarn(it,'missingLeadingZero')" class="badge badge-warning">{{ $t('arWarnZero') }}</span>
                      <span v-if="hasWarn(it,'normalized84')" class="badge badge-light border">{{ $t('arWarn84') }}</span>
                      <span v-if="it.reportedTimes && it.reportedTimes.length > 1" class="badge badge-info">{{ $t('arMultiTimes', [it.reportedTimes.length]) }}</span>
                    </td>
                    <td>
                      <b-form-input type="datetime-local" step="1" v-model="it.timeLocal" size="sm" :disabled="!isNew" style="min-width: 200px"></b-form-input>
                      <span v-if="hasWarn(it,'badDate')" class="badge badge-danger">{{ $t('arWarnBadDate') }}</span>
                      <span v-else-if="hasWarn(it,'futureTime')" class="badge badge-danger">{{ $t('arWarnFuture') }}</span>
                      <span v-else-if="!it.timeLocal && !(it.dateRange && it.dateRange.from)" class="badge badge-warning">{{ $t('arWarnNoTime') }}</span>
                      <span v-else-if="!it.timeLocal" class="small text-muted">{{ it.dateRange.from | moment('DD/MM') }} – {{ it.dateRange.to | moment('DD/MM/YYYY') }}</span>
                    </td>
                    <td><b-form-select v-model="it.type" size="sm" :options="itemTypeOptions" :disabled="!isNew"></b-form-select></td>
                    <td><b-button size="sm" variant="link" class="text-danger" :disabled="!isNew" @click="form.items.splice(i, 1)"><i class="fa fa-times"></i></b-button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="mt-2 d-flex justify-content-between" v-if="isNew">
              <b-button size="sm" variant="outline-secondary" @click="form.items.push({reportedNumber: '', timeLocal: '', type: null, warnings: [], caller: ''})">＋ {{ $t('arAddRow') }}</b-button>
              <b-button variant="primary" :disabled="creating || !form.items.length" @click="create">{{ creating ? $t('arCreating') : $t('arCreate') }} →</b-button>
            </div>
          </div>
        </div>
      </div>
      <div class="text-right" v-else-if="isNew">
        <b-button variant="primary" :disabled="creating" @click="create">{{ creating ? $t('arCreating') : $t('arCreate') }} →</b-button>
      </div>
      <div class="text-right" v-if="!isNew"><b-button variant="primary" @click="step = 2">{{ $t('arStep2') }} →</b-button></div>
    </div>

    <!-- ================= BƯỚC 2: TRA ================= -->
    <div v-if="step === 2 && doc">
      <!-- R3: cuộc gọi trong phạm vi -->
      <div v-if="doc.requestType === 'recordingsByNumber'" class="tile">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">{{ $t('arScopeCalls') }} <span class="text-muted small">({{ scope.total }})</span> — {{ doc.scope.number }} · {{ doc.scope.client && doc.scope.client.domain }} · {{ doc.scope.from | moment('DD/MM/YYYY') }} – {{ doc.scope.to | moment('DD/MM/YYYY') }}</h5>
          <div>
            <b-button size="sm" :variant="allScope ? 'primary' : 'outline-secondary'" @click="allScope = !allScope; selectedCalls = []">{{ $t('arSelectWholeScope', [scope.total]) }}</b-button>
            <b-button size="sm" variant="outline-secondary" class="ml-1" :disabled="allScope" @click="selectAllScope">{{ $t('arSelectPage') }}</b-button>
            <span class="small text-muted ml-2">{{ allScope ? $t('arSelectedAll', [scope.total]) : $t('arSelected', [selectedCalls.length]) }}</span>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead><tr><th></th><th>{{ $t('arColTime') }}</th><th>{{ $t('arDir') }}</th><th>{{ $t('arFrom') }}</th><th>{{ $t('arTo') }}</th><th>{{ $t('arTalk') }}</th><th>{{ $t('arRecording') }}</th></tr></thead>
            <tbody>
              <tr v-for="c in scope.docs" :key="c.call">
                <td><b-form-checkbox :checked="allScope || selectedCalls.includes(c.call)" :disabled="allScope" @change="toggleCall(c.call)"></b-form-checkbox></td>
                <td class="small">{{ c.startTime | moment('DD/MM/YYYY HH:mm:ss') }}</td>
                <td><span class="badge" :class="c.direction === 'inbound' ? 'badge-info' : 'badge-secondary'">{{ c.direction }}</span></td>
                <td>{{ c.fromNumber }}</td><td>{{ c.toNumber }}</td>
                <td class="small">{{ c.billDuration || 0 }}s</td>
                <td><recording-cell :call="c" :request-id="doc._id"></recording-cell></td>
              </tr>
            </tbody>
          </table>
        </div>
        <b-pagination v-if="scope.total > scope.limit" size="sm" class="mt-2 mb-0" v-model="scope.page" :total-rows="scope.total" :per-page="scope.limit" @input="loadScope"></b-pagination>
      </div>

      <!-- R1/R2/R4: từng dòng -->
      <div v-else class="tile">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">{{ $t('arStep2') }} <span class="small text-muted">· {{ doc.sourceNumber }}</span></h5>
          <div>
            <b-button size="sm" variant="primary" :disabled="!!lookupProgress" @click="lookupAll(false)">{{ $t('arLookupAll') }}</b-button>
            <b-button size="sm" variant="outline-secondary" class="ml-1" :disabled="!!lookupProgress" @click="lookupAll(true)">{{ $t('arRelookup') }}</b-button>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-sm mb-0 ar-table">
            <thead><tr><th>#</th><th>{{ $t('arColNumber') }}</th><th>{{ $t('arResult') }}</th><th>{{ $t('arDomain') }}</th><th>{{ $t('arCall') }}</th><th>{{ $t('arTalk') }}</th><th>{{ $t('arRecording') }}</th><th>{{ $t('arPrior') }}</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(it, i) in doc.items" :key="it._id" :class="rowClass(it)">
                <td>{{ i + 1 }}</td>
                <td><div><b>{{ it.reportedNumber }}</b></div><div class="small text-muted">{{ it.reportedTime ? fmt(it.reportedTime) : (it.dateRange && it.dateRange.from ? fmtD(it.dateRange.from) + ' – ' + fmtD(it.dateRange.to) : '') }} <span v-if="it.type" class="badge" :class="it.type === 'fraud' ? 'badge-danger' : 'badge-warning'">{{ $t('arType_' + it.type) }}</span></div></td>
                <td>
                  <span class="badge" :class="matchClass(it)">{{ it.matchStatus === 'one' && it.unanswered ? $t('arUnanswered') : $t('arMatch_' + (it.matchStatus || 'pending')) }}</span>
                  <div class="small text-muted" v-if="it.searchWindow && it.searchWindow.label">{{ $t('arWindow') }} {{ it.searchWindow.label }}</div>
                </td>
                <td>
                  <template v-if="it.matchStatus === 'many'">
                    <div v-for="c in it.candidates" :key="c.call" class="small">
                      <b-form-radio :checked="it.call === c.call" :value="c.call" @change="pickCandidate(i, c.call)">
                        <b>{{ c.client && c.client.domain }}</b> <span class="text-muted">{{ fmt(c.startTime) }} · {{ c.billDuration || 0 }}s{{ c.fromUser ? ' · ' + c.fromUser.name : '' }} · {{ $t('arOffset', [offsetLabel(it, c)]) }}</span>
                      </b-form-radio>
                    </div>
                  </template>
                  <template v-else-if="it.client">
                    <div><b>{{ it.client.domain }}</b></div>
                    <div class="small text-muted" v-if="it.fromUser">{{ it.fromUser.name }}<span v-if="it.fromUser.ext"> ({{ it.fromUser.ext }})</span></div>
                    <div class="small" v-if="it.faultKeywords && it.faultKeywords.length"><span class="text-muted">{{ $t('arKeywords') }}:</span> <span v-for="k in it.faultKeywords" :key="k" class="badge badge-danger mr-1">{{ k }}</span></div>
                  </template>
                  <span v-else class="text-muted small">—</span>
                </td>
                <td class="small">
                  <template v-if="chosen(it)">{{ fmt(chosen(it).startTime) }}<div class="text-muted">{{ chosen(it).fromNumber }} → {{ chosen(it).toNumber }}</div><div v-if="chosen(it).hangupCause" class="text-muted">{{ chosen(it).hangupCause }}</div></template>
                  <span v-else-if="it.matchStatus === 'none' && it.candidates && it.candidates.length" class="text-muted">{{ $t('arOtherCaller', [it.candidates.length]) }}</span>
                  <span v-else>—</span>
                </td>
                <td class="small">{{ chosen(it) ? (chosen(it).billDuration || 0) + 's' : '—' }}</td>
                <td><recording-cell v-if="chosen(it)" :call="chosen(it)" :request-id="doc._id"></recording-cell><span v-else class="text-muted small">—</span></td>
                <td class="small"><span v-if="it.client" class="badge" :class="it.priorCount ? 'badge-warning' : 'badge-light border'">{{ it.priorCount ? $t('arPriorN', [it.priorCount]) : $t('arPriorFirst') }}</span></td>
                <td><b-button size="sm" variant="link" :disabled="!!lookupProgress" @click="lookupOne(i)"><i class="fa fa-refresh"></i></b-button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-for="(it, i) in doc.items" :key="'u' + it._id">
          <div v-if="it.matchStatus === 'one' && it.unanswered" class="alert alert-warning py-2 mt-2 mb-0 small">{{ $t('arUnansweredHint', [i + 1]) }}</div>
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <b-button variant="secondary" @click="step = 1">← {{ $t('arStep1') }}</b-button>
        <b-button variant="primary" @click="step = 3">{{ $t('arStep3') }} →</b-button>
      </div>
    </div>

    <!-- ================= BƯỚC 3: KẾT LUẬN & XUẤT ================= -->
    <div v-if="step === 3 && doc">
      <div class="tile" v-if="doc.requestType !== 'recordingsByNumber'">
        <h5>{{ $t('arVerdict') }}</h5>
        <div class="table-responsive">
          <table class="table table-sm mb-0">
            <thead><tr><th>#</th><th>{{ $t('arColNumber') }} · {{ $t('arDomain') }}</th><th style="min-width: 200px">{{ $t('arVerdict') }}</th><th style="min-width: 260px">{{ $t('arNote') }}</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(it, i) in doc.items" :key="it._id">
                <td>{{ i + 1 }}</td>
                <td><b>{{ it.reportedNumber }}</b> <span v-if="it.client">· {{ it.client.domain }}</span><span v-else class="text-muted"> · {{ $t('arMatch_' + (it.matchStatus || 'pending')) }}</span>
                  <div v-if="it.matchStatus === 'one' && it.unanswered" class="small text-warning">{{ $t('arUnanswered') }}</div></td>
                <td>
                  <b-form-select :value="it.verdict" size="sm" :options="verdictOptions" :class="'ar-v-' + (it.verdict || 'none')" @change="v => saveItem(i, {verdict: v})"></b-form-select>
                  <div v-if="it.notifiedAt" class="small text-success">{{ $t('arNotified') }} {{ it.notifiedAt | moment('DD/MM HH:mm') }}</div>
                </td>
                <td><b-form-textarea :value="it.note" rows="1" size="sm" @change="v => saveItem(i, {note: v})"></b-form-textarea></td>
                <td class="small text-muted">
                  <span v-if="it.history && it.history.length" v-b-tooltip.hover :title="it.history.map(h => fmt(h.updateTime) + ' ' + (h.updatedBy && h.updatedBy.name || '') + ': ' + (h.verdict ? $t('arVerdict_' + h.verdict) : '—')).join(' · ')">{{ $t('arHistory') }} ({{ it.history.length }})</span>
                  <div v-if="it.client" class="mt-1">
                    <a :href="'/client?keyword=' + it.client.domain" target="_blank" class="mr-2" @click="auditLink('openClient', it.client.domain)">{{ $t('arOpenClient') }}</a>
                    <a :href="'/number?keyword=' + it.client.domain" target="_blank" @click="auditLink('openNumbers', it.client.domain)">{{ $t('arOpenNumbers') }}</a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="small text-muted mt-2">{{ $t('arVerdictHint') }}</div>
        <div v-if="itemWarning" class="alert alert-warning py-2 mt-2 mb-0 small">{{ $t('arWarnExported') }}</div>
      </div>

      <div class="row">
        <div class="col-lg-6">
          <div class="tile">
            <h5>{{ $t('arExports') }}</h5>
            <div class="mb-2">
              <b-form-checkbox v-model="exportForm.cdrExcel">{{ $t('arKindCdr') }}</b-form-checkbox>
              <b-form-checkbox v-model="exportForm.kycXlsx">{{ $t('arKindKyc') }}</b-form-checkbox>
              <div class="mt-2 mb-1 font-weight-bold small">{{ $t('arRecordingMode') }}</div>
              <b-form-radio-group v-model="exportForm.recordingMode" stacked>
                <b-form-radio value="recordingLinks">{{ $t('arKindLinks') }} <span class="text-muted">{{ $t('arKindLinksHint') }}</span></b-form-radio>
                <b-form-radio value="recordingsZip">{{ $t('arKindZip') }} <span class="text-muted">{{ $t('arKindZipHint') }}</span></b-form-radio>
                <b-form-radio value="">{{ $t('arKindNoRecording') }}</b-form-radio>
              </b-form-radio-group>
            </div>
            <div class="small text-muted mb-2">{{ $t('arExportHint') }}<span v-if="doc.requestType !== 'recordingsByNumber'"> {{ $t('arExportScopeHint') }}</span></div>
            <div v-if="exportBlocked" class="alert alert-info py-2 small">{{ $t('arExportBlocked') }}</div>
            <b-button variant="primary" :disabled="exportBlocked || !exportKinds.length || exporting" @click="createExport">{{ $t('arCreateExport') }}</b-button>

            <div v-if="doc.exports.length" class="table-responsive mt-3">
              <table class="table table-sm mb-0 ar-exports">
                <thead><tr><th>{{ $t('arExports') }}</th><th>{{ $t('arStatus') }}</th><th>{{ $t('arApprover') }}</th><th>{{ $t('arDownload') }}</th></tr></thead>
                <tbody>
                  <tr v-for="ex in doc.exports.slice().reverse()" :key="ex._id">
                    <td class="small">
                      <div class="text-nowrap">{{ ex.kinds.map(k => $t('arFile_' + k)).join(' + ') }}<span v-if="ex.callIds.length"> · {{ $t('arCalls', [ex.callIds.length]) }}</span></div>
                      <div class="text-muted text-nowrap">{{ $t('arCreatedBy') }} {{ ex.createdBy && ex.createdBy.name }} · {{ ex.createTime | moment('DD/MM HH:mm') }}</div>
                      <div v-if="ex.errorMessages && ex.errorMessages.length" class="text-danger">{{ ex.errorMessages.join('; ') }}</div>
                    </td>
                    <td class="text-nowrap">
                      <span class="badge" :class="exportClass(ex.status)">{{ $t('arExportStatus_' + ex.status) }}</span>
                      <div v-if="ex.status === 'waitingRestore'" class="small text-info">{{ ex.waitingRestoreCalls.length }} {{ $t('arFilesWaiting') }}</div>
                    </td>
                    <td class="small">
                      <div v-if="ex.approvedBy" class="text-nowrap">{{ ex.approvedBy.name }} · {{ ex.approvedAt | moment('DD/MM HH:mm') }}</div>
                      <div v-if="ex.downloadedBy && ex.downloadedBy.length" class="text-muted">{{ $t('arDownloadedBy') }}: {{ ex.downloadedBy.map(d => (d.user && d.user.name || '') + ' ' + fmtShort(d.time)).join('; ') }}</div>
                      <b-button v-if="ex.status === 'awaitingApproval' && canApprove && !isCreator(ex)" size="sm" variant="success" class="text-nowrap" @click="approve(ex)">{{ $t('arApprove') }}</b-button>
                      <span v-else-if="ex.status === 'awaitingApproval'" class="text-muted">{{ isCreator(ex) ? $t('arNeedOtherApprover') : $t('arNeedApproverRole') }}</span>
                    </td>
                    <td class="text-nowrap">
                      <template v-if="ex.status === 'approved'">
                        <b-button v-for="f in ex.files" :key="f.kind" size="sm" variant="outline-primary" class="mr-1 mb-1" @click="download(ex, f.kind)"><i class="fa fa-download"></i> {{ $t('arFile_' + f.kind) }}<span v-if="f.count"> ({{ f.count }})</span></b-button>
                      </template>
                      <b-button v-else-if="['expired', 'failed'].includes(ex.status) && !exportBlocked" size="sm" variant="outline-secondary" @click="recreate(ex)">{{ $t('arRecreate') }}</b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="tile">
            <div class="d-flex justify-content-between align-items-center"><h5 class="mb-0">{{ $t('arReply') }}</h5><b-button size="sm" variant="link" @click="loadReply(true)">{{ $t('arReplyGen') }}</b-button></div>
            <b-form-textarea v-model="reply" rows="14" class="mt-2" style="font-family: monospace; font-size: 12.5px" @change="saveReply"></b-form-textarea>
            <div class="mt-2 d-flex flex-wrap">
              <b-button size="sm" variant="secondary" class="mr-1 mb-1" @click="copyReply">{{ copied ? $t('arCopied') : $t('arCopy') }}</b-button>
              <b-button size="sm" variant="secondary" class="mr-1 mb-1" :disabled="!doc.ticket || posting" @click="postTicket">{{ posted ? $t('arPosted') : $t('arToTicket') }}<span v-if="doc.ticket"> {{ doc.ticket.code }}</span></b-button>
              <b-button v-if="doc.status !== 'answered'" size="sm" variant="success" class="mb-1" @click="markAnswered">{{ $t('arMarkAnswered') }}</b-button>
              <b-button v-else size="sm" variant="outline-secondary" class="mb-1" @click="reopen">{{ $t('arReopen') }}</b-button>
            </div>
          </div>
        </div>
      </div>

      <div class="tile">
        <h5 class="small text-uppercase text-muted">{{ $t('arAudit') }}</h5>
        <div class="small" v-for="(a, i) in doc.audit.slice().reverse().slice(0, 30)" :key="i">
          <span class="text-muted">{{ fmt(a.time) }}</span> · <b>{{ a.user && a.user.name }}</b> · {{ $te('arAudit_' + a.action) ? $t('arAudit_' + a.action) : a.action }} <span class="text-muted">{{ a.detail }}</span>
        </div>
      </div>
      <div><b-button variant="secondary" @click="step = 2">← {{ $t('arStep2') }}</b-button></div>
    </div>
  </main>
</template>

<script>
import moment from 'moment-timezone';
import ClientSelect from '@/components/ClientSelect';
import RecordingCell from './AuthorityRecordingCell';
import arI18n from './authority-request-i18n';

const TZ = 'Asia/Ho_Chi_Minh';
const toLocal = (t) => t ? moment(t).tz(TZ).format('YYYY-MM-DDTHH:mm:ss') : '';
const fromLocal = (s) => s ? moment.tz(s, TZ).valueOf() : 0;
const BLOCKING = ['pending', 'processing', 'waitingRestore', 'awaitingApproval'];

export default {
  i18n: arI18n,
  components: {ClientSelect, RecordingCell},
  data() {
    return {
      step: 1, doc: null, error: '', creating: false, exporting: false, posting: false, posted: false, copied: false,
      pools: [], poolPick: null, ticketExisting: [], lookupProgress: null, pollTimer: null, exportTimer: null, itemWarning: false,
      form: {source: 'carrier', sourceName: '', requestType: 'identify', reference: '', dueLocal: '', sourceNumber: '', ticketCode: '', rawText: '', inputFileName: '', items: [], rangeIsExplicit: false, scope: {client: null, number: '', fromLocal: '', toLocal: ''}},
      allScope: false,
      scope: {docs: [], total: 0, page: 1, limit: 100}, selectedCalls: [],
      exportForm: {cdrExcel: true, kycXlsx: false, recordingMode: 'recordingLinks'}, // ghi âm mặc định gửi LINK (không sao chép file, rẻ hơn zip)
      reply: '',
    };
  },
  computed: {
    isNew() { return this.$route.params.id === 'new'; },
    user() { return this.$store.state.user || {}; },
    canApprove() { return ['owner', 'admin', 'tech'].includes(this.user.role); },
    sourceOptions() { return ['carrier', 'attt', 'police', 'customer', 'other'].map(v => ({value: v, text: this.sourceLabel(v)})); },
    typeOptions() { return ['identify', 'recordingsByCdr', 'recordingsByNumber', 'bulkIdentify', 'other'].map(v => ({value: v, text: this.$t('arType_' + v)})); },
    itemTypeOptions() { return [{value: null, text: this.$t('arType_none')}, ...['fraud', 'spam', 'intlFraud', 'defamation', 'other'].map(v => ({value: v, text: this.$t('arType_' + v)}))]; },
    verdictOptions() { return [{value: null, text: this.$t('arVerdict_none')}, ...['confirmed', 'notCallio', 'insufficient', 'falseReport'].map(v => ({value: v, text: this.$t('arVerdict_' + v)}))]; },
    poolOptions() { return [{value: null, text: this.$t('arPool')}, ...this.pools.map(p => ({value: p.number, text: p.number + (p.carrier ? ' · ' + p.carrier : '')}))]; },
    hasCaller() { return this.form.items.some(i => i.caller); },
    exportKinds() { return ['cdrExcel', 'kycXlsx'].filter(k => this.exportForm[k]).concat(this.exportForm.recordingMode ? [this.exportForm.recordingMode] : []); },
    exportBlocked() { return !!(this.doc && this.doc.exports.some(e => BLOCKING.includes(e.status))); },
  },
  watch: {
    '$route.params.id'() { this.init(); },
  },
  created() { this.init(); },
  beforeDestroy() { this.stopPoll(); this.stopExportPoll(); },
  methods: {
    fmt(t) { return t ? moment(t).tz(TZ).format('DD/MM/YYYY HH:mm:ss') : ''; },
    fmtD(t) { return t ? moment(t).tz(TZ).format('DD/MM/YYYY') : ''; },
    fmtShort(t) { return t ? moment(t).tz(TZ).format('DD/MM HH:mm') : ''; },
    // độ lệch giữa giờ cuộc gọi và mốc phản ánh (giúp chọn đúng ứng viên khi `many`)
    offsetLabel(it, c) {
      const ref = it.reportedTime || (it.dateRange && it.dateRange.from) || 0;
      if (!ref || !c.startTime) return '—';
      const m = Math.round(Math.abs(c.startTime - ref) / 60000);
      return m < 60 ? m + "'" : m < 1440 ? Math.round(m / 60) + 'h' : Math.round(m / 1440) + 'd';
    },
    auditLink(action, detail) { this.$service.post(`/authority-request/${this.doc._id}/audit`, {action, detail}).catch(() => {}); },
    sourceLabel(v) { return this.$t('arSource_' + (v || 'other')); },
    hasWarn(it, w) { return (it.warnings || []).includes(w); },
    chosen(it) { return it.call ? (it.candidates || []).find(c => String(c.call) === String(it.call && it.call._id ? it.call._id : it.call)) : null; },
    rowClass(it) { return it.matchStatus === 'many' ? 'table-warning' : (it.matchStatus === 'none' ? 'table-light' : (it.unanswered ? 'table-warning' : '')); },
    matchClass(it) { return {one: it.unanswered ? 'badge-warning' : 'badge-success', many: 'badge-warning', none: 'badge-danger', pending: 'badge-light border', error: 'badge-danger'}[it.matchStatus || 'pending'] || 'badge-light border'; },
    exportClass(s) { return {pending: 'badge-light border', processing: 'badge-info', waitingRestore: 'badge-info', awaitingApproval: 'badge-warning', approved: 'badge-success', failed: 'badge-danger', expired: 'badge-secondary'}[s] || 'badge-light'; },
    isCreator(ex) { return ex.createdBy && String(ex.createdBy._id || ex.createdBy) === String(this.user._id); },
    fail(e) { this.error = (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || this.$t('arError'); },
    async init() {
      this.stopPoll(); this.stopExportPoll();
      Object.assign(this, {error: '', doc: null, ticketExisting: [], lookupProgress: null, itemWarning: false, selectedCalls: [], allScope: false, reply: '', posted: false, copied: false, scope: {docs: [], total: 0, page: 1, limit: 100}});
      this.$service.get('/authority-request/pools').then(r => { this.pools = r.data || []; }).catch(() => {});
      if (this.isNew) { this.step = 1; this.doc = null; return; }
      await this.load();
      if (this.doc) {
        this.step = this.doc.items.some(i => i.matchStatus === 'pending') && this.doc.requestType !== 'recordingsByNumber' ? 2 : (this.doc.status === 'answered' ? 3 : 2);
        this.fillForm();
        if (this.doc.requestType === 'recordingsByNumber') this.loadScope();
        if (this.doc.items.some(i => i.matchStatus === 'pending') && this.doc.requestType !== 'recordingsByNumber') this.lookupAll(false);
        this.loadReply(false);
      }
    },
    fillForm() {
      const d = this.doc;
      this.form = {
        source: d.source, sourceName: d.sourceName, requestType: d.requestType, reference: d.reference, dueLocal: toLocal(d.dueTime), sourceNumber: d.sourceNumber,
        ticketCode: d.ticket ? d.ticket.code : '', rawText: d.rawText, inputFileName: d.inputFileName,
        items: d.items.map(it => ({...it, timeLocal: toLocal(it.reportedTime)})),
        scope: {client: d.scope && d.scope.client ? (d.scope.client._id || d.scope.client) : null, number: d.scope ? d.scope.number : '', fromLocal: d.scope && d.scope.from ? moment(d.scope.from).tz(TZ).format('YYYY-MM-DD') : '', toLocal: d.scope && d.scope.to ? moment(d.scope.to).tz(TZ).format('YYYY-MM-DD') : ''},
      };
    },
    async load() {
      try { const r = await this.$service.get('/authority-request/' + this.$route.params.id); this.doc = r.data; } catch (e) { this.fail(e); }
    },
    // ─── bước 1 ───
    async parseText() {
      this.error = '';
      try {
        const r = await this.$service.post('/authority-request/parse', {text: this.form.rawText});
        if (r.data.sourceNumber && !this.form.sourceNumber) this.form.sourceNumber = r.data.sourceNumber;
        this.form.rangeIsExplicit = !!r.data.rangeIsExplicit;
        this.form.items = r.data.items.map(it => ({...it, timeLocal: toLocal(it.reportedTime)}));
      } catch (e) { this.fail(e); }
    },
    async parseFile(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      this.error = '';
      const fd = new FormData(); fd.append('file', file);
      try {
        const r = await this.$service.post('/authority-request/parse-file', fd, {headers: {'Content-Type': 'multipart/form-data'}});
        this.form.items = r.data.items.map(it => ({...it, timeLocal: toLocal(it.reportedTime)}));
        this.form.inputFileName = r.data.fileName || file.name;
        if (r.data.skipped && r.data.skipped.length) this.error = this.$t('arSkippedRows', [r.data.skipped.length]) + ' ' + r.data.skipped.slice(0, 5).join('; ');
        if (this.form.requestType === 'identify') this.form.requestType = 'recordingsByCdr';
      } catch (e) { this.fail(e); }
      ev.target.value = '';
    },
    async loadTicket() {
      this.error = ''; this.ticketExisting = [];
      if (!this.form.ticketCode) return;
      try {
        const r = await this.$service.get('/authority-request/check-ticket', {params: {code: this.form.ticketCode}});
        this.ticketExisting = r.data.existing || [];
        if (!this.form.rawText) this.form.rawText = r.data.ticket.desc || '';
        if (!this.form.reference) this.form.reference = r.data.ticket.code + ' · ' + r.data.ticket.name;
      } catch (e) { this.fail(e); }
    },
    async create() {
      this.error = ''; this.creating = true;
      try {
        const f = this.form;
        const body = {
          source: f.source, sourceName: f.sourceName, requestType: f.requestType, reference: f.reference, dueTime: fromLocal(f.dueLocal),
          sourceNumber: f.sourceNumber, ticketCode: f.ticketCode, rawText: f.rawText, inputFileName: f.inputFileName,
          // giữ nguyên nhiều mốc (T-13656: 1 số 2 mốc) khi người dùng không sửa mốc đầu; sửa tay → chỉ 1 mốc
          items: f.items.map(it => {
            const t = fromLocal(it.timeLocal);
            const keep = it.reportedTimes && it.reportedTimes.length && t === it.reportedTimes[0];
            return {caller: it.caller, reportedNumber: it.reportedNumber, reportedTime: t, reportedTimes: keep ? it.reportedTimes : (t ? [t] : []), dateRange: it.dateRange, billsec: it.billsec, type: it.type, warnings: it.warnings};
          }),
          rangeIsExplicit: f.rangeIsExplicit,
          scope: {client: f.scope.client, number: f.scope.number, from: f.scope.fromLocal ? moment.tz(f.scope.fromLocal + ' 00:00:00', TZ).valueOf() : 0, to: f.scope.toLocal ? moment.tz(f.scope.toLocal + ' 23:59:59', TZ).valueOf() : 0},
        };
        const r = await this.$service.post('/authority-request', body);
        this.$router.replace('/authority-request/' + r.data._id);
      } catch (e) { this.fail(e); }
      this.creating = false;
    },
    // ─── bước 2 ───
    async lookupAll(force) {
      this.error = '';
      try {
        const r = await this.$service.post('/authority-request/' + this.doc._id + '/lookup' + (force ? '?force=1' : ''));
        if (r.data.started || r.data.running) {
          this.lookupProgress = {done: 0, total: r.data.pending};
          this.startPoll();
        }
      } catch (e) { this.fail(e); }
    },
    // poll tiến độ gọn (/progress), chỉ tải cả hồ sơ khi xong hoặc mỗi 10 s để bảng cập nhật dần
    startPoll() {
      this.stopPoll();
      let ticks = 0;
      this.pollTimer = setInterval(async () => {
        try {
          const r = await this.$service.get(`/authority-request/${this.$route.params.id}/progress`);
          const p = r.data || {};
          if (this.lookupProgress) this.lookupProgress.done = (p.total || 0) - (p.pending || 0);
          ticks += 1;
          if (!p.pending || !p.running) { await this.load(); this.lookupProgress = null; this.stopPoll(); }
          else if (ticks % 5 === 0) await this.load();
        } catch (e) { this.stopPoll(); this.lookupProgress = null; }
      }, 2000);
    },
    stopPoll() { if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; } },
    stopExportPoll() { if (this.exportTimer) { clearInterval(this.exportTimer); this.exportTimer = null; } },
    async lookupOne(i) {
      this.error = '';
      try { const r = await this.$service.post(`/authority-request/${this.doc._id}/lookup-item/${i}`); this.doc = r.data; } catch (e) { this.fail(e); }
    },
    async pickCandidate(i, callId) { await this.saveItem(i, {callId}); },
    async loadScope() {
      try { const r = await this.$service.get(`/authority-request/${this.doc._id}/scope-calls`, {params: {page: this.scope.page}}); this.scope = {...this.scope, ...r.data}; } catch (e) { this.fail(e); }
    },
    toggleCall(id) { const i = this.selectedCalls.indexOf(id); if (i >= 0) this.selectedCalls.splice(i, 1); else this.selectedCalls.push(id); },
    selectAllScope() { this.scope.docs.forEach(c => { if (!this.selectedCalls.includes(c.call)) this.selectedCalls.push(c.call); }); },
    // ─── bước 3 ───
    async saveItem(i, body) {
      this.error = ''; this.itemWarning = false;
      try {
        const r = await this.$service.put(`/authority-request/${this.doc._id}/items/${i}`, body);
        this.itemWarning = !!(r.data.warnings && r.data.warnings.length);
        this.doc = r.data;
      } catch (e) { this.fail(e); await this.load(); }
    },
    async createExport() {
      this.error = ''; this.exporting = true;
      try {
        const callIds = this.doc.requestType === 'recordingsByNumber' ? (this.allScope ? [] : this.selectedCalls) : undefined;
        if (this.doc.requestType === 'recordingsByNumber' && !this.allScope && !this.selectedCalls.length) { this.error = this.$t('arPickForExport'); this.exporting = false; return; }
        const r = await this.$service.post(`/authority-request/${this.doc._id}/exports`, {kinds: this.exportKinds, callIds});
        this.doc = r.data; this.startExportPoll();
      } catch (e) { this.fail(e); }
      this.exporting = false;
    },
    startExportPoll() {
      this.stopExportPoll();
      this.exportTimer = setInterval(async () => {
        await this.load();
        if (!this.doc || !this.doc.exports.some(e => ['pending', 'processing'].includes(e.status))) this.stopExportPoll();
      }, 5000);
    },
    async approve(ex) {
      this.error = '';
      try { const r = await this.$service.post(`/authority-request/${this.doc._id}/exports/${ex._id}/approve`); this.doc = r.data; this.loadReply(false); } catch (e) { this.fail(e); await this.load(); }
    },
    async download(ex, kind) {
      this.error = '';
      const w = window.open('', '_blank'); // mở tab TRƯỚC await — Safari chặn popup sau async
      try { const r = await this.$service.get(`/authority-request/${this.doc._id}/exports/${ex._id}/url`, {params: {kind}}); if (w) w.location = r.data.url; else window.open(r.data.url, '_blank'); await this.load(); } catch (e) { if (w) w.close(); this.fail(e); await this.load(); }
    },
    async recreate(ex) {
      this.error = '';
      try { const r = await this.$service.post(`/authority-request/${this.doc._id}/exports/${ex._id}/recreate`); this.doc = r.data; this.startExportPoll(); } catch (e) { this.fail(e); }
    },
    async loadReply(regen) {
      try { const r = await this.$service.get(`/authority-request/${this.doc._id}/reply`); this.reply = regen ? r.data.generated : r.data.text; if (regen) this.saveReply(); } catch (e) { /* bỏ qua */ }
    },
    async saveReply() { try { await this.$service.put(`/authority-request/${this.doc._id}/reply`, {text: this.reply}); } catch (e) { this.fail(e); } },
    copyReply() {
      const done = () => { this.copied = true; setTimeout(() => { this.copied = false; }, 2000); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(this.reply).then(done).catch(() => this.fallbackCopy(done));
      else this.fallbackCopy(done);
    },
    fallbackCopy(done) { const ta = document.createElement('textarea'); ta.value = this.reply; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); done(); } catch (e) { /* bỏ qua */ } document.body.removeChild(ta); },
    async postTicket() {
      this.error = ''; this.posting = true;
      try { await this.saveReply(); await this.$service.post(`/authority-request/${this.doc._id}/ticket-comment`, {text: this.reply}); this.posted = true; await this.load(); } catch (e) { this.fail(e); }
      this.posting = false;
    },
    async markAnswered() { this.error = ''; try { await this.saveReply(); const r = await this.$service.post(`/authority-request/${this.doc._id}/answered`); this.doc = r.data; } catch (e) { this.fail(e); } },
    async reopen() { this.error = ''; try { const r = await this.$service.post(`/authority-request/${this.doc._id}/reopen`); this.doc = r.data; } catch (e) { this.fail(e); } },
  },
};
</script>

<style scoped>
.ar-steps .ar-step { flex: 1; padding: 8px 12px; border: 1px solid #dee2e6; background: #fff; color: #6c757d; cursor: pointer; display: flex; align-items: center; gap: 8px; }
.ar-steps .ar-step + .ar-step { border-left: 0; }
.ar-steps .ar-step.on { color: #007bff; font-weight: 600; background: #e7f1fb; }
.ar-steps .ar-step.done { color: #28a745; }
.ar-steps .ar-step .n { display: inline-flex; width: 20px; height: 20px; border: 1px solid currentColor; border-radius: 50%; align-items: center; justify-content: center; font-size: 11px; margin-right: 6px; }
.ar-v-confirmed { color: #dc3545; font-weight: 600; }
.ar-v-notCallio { color: #6c757d; font-weight: 600; }
.ar-v-insufficient { color: #b8860b; font-weight: 600; }
</style>
